import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";

import { AdminScreen, Field, Section, UploadButton } from "@/admin/kit";
import { api, relUrl } from "@/lib/api";
import { Button } from "@/components/kit";
import { useApp } from "@/store";

function LegalEditor() {
  const { toast } = useApp();
  const [d, setD] = useState(null);
  useEffect(() => {
    api("/legal").then(setD).catch((e) => toast(e.message, "error"));
  }, [toast]);
  if (!d) return null;
  const save = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.privacy_contact.trim())) {
      toast("Enter a valid privacy contact email (e.g. privacy@breez.au).", "error");
      return;
    }
    try {
      setD(await api("/admin/legal", { method: "PUT", body: { ...d, privacy_contact: d.privacy_contact.trim() } }));
      toast("Legal pages & privacy contact saved.", "success");
    } catch (e) {
      toast(e.message, "error");
    }
  };
  return (
    <Section title="Privacy contact & legal pages" testId="admin-legal" note="Shown in the Privacy centre. The seeded text is a DRAFT — BREEZ must review it before launch.">
      <Field testId="admin-legal-contact" label="Privacy contact email" value={d.privacy_contact} onChange={(privacy_contact) => setD({ ...d, privacy_contact })} />
      <Field testId="admin-legal-terms" label="Terms of use" value={d.terms} onChange={(terms) => setD({ ...d, terms })} multiline />
      <Field testId="admin-legal-privacy" label="Privacy policy" value={d.privacy_policy} onChange={(privacy_policy) => setD({ ...d, privacy_policy })} multiline />
      <Button testId="admin-legal-save" title="Save legal pages" icon={Check} onClick={save} className="self-start" />
    </Section>
  );
}

export default function AdminBrand() {
  const { brandLogo, refreshBrand, toast } = useApp();
  const save = async (url) => {
    try {
      await api("/admin/brand", { method: "PUT", body: { logo_url: url ? relUrl(url) : null } });
      refreshBrand();
      toast(url ? "Logo updated everywhere." : "Logo removed — showing the text wordmark.", "success");
    } catch (e) {
      toast(e.message, "error");
    }
  };
  return (
    <AdminScreen title="Brand, privacy & legal" testId="admin-brand">
      <Section title="Official BREEZ logo" note="Replaces the text wordmark on the welcome screen, Home header and Share card. Use a transparent PNG/WEBP (≤10MB) that reads well on navy and white.">
        <div className="flex flex-wrap gap-3">
          {["bg-navy", "bg-white"].map((bg, i) => (
            <div key={bg} data-testid={`admin-brand-preview-${i}`} className={`${bg} min-w-[220px] rounded-xl border border-line p-5`}>
              {brandLogo ? <img src={brandLogo} alt="BREEZ logo" className="h-10 w-40 object-contain" /> : (
                <span className={`font-display text-2xl tracking-[6px] ${i ? "text-navy" : "text-white"}`}>BREEZ</span>
              )}
            </div>
          ))}
        </div>
        <p data-testid="admin-brand-status" className="t-caption">{brandLogo ? "Custom logo is live." : "No logo set — the text wordmark is shown."}</p>
        <UploadButton kind="image" title={brandLogo ? "Replace logo" : "Upload logo"} testId="admin-brand-upload" onUploaded={(u) => save(u.url)} onError={(m) => toast(m, "error")} />
        {brandLogo ? <Button testId="admin-brand-remove" title="Remove logo" variant="ghost" onClick={() => save(null)} className="self-start" /> : null}
      </Section>
      <LegalEditor />
    </AdminScreen>
  );
}
